import React, { useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import LandingNav from './LandingNav';
import LandingFooter from './LandingFooter';
import HeroAccumulation from './HeroAccumulation';
import EaseStepperSection from './EaseStepperSection';
import TwoWaysSection from './TwoWaysSection';
import SeoMeta from '../SeoMeta';
import { track } from '../../services/analytics';
import './LandingPage.css';

/* Scroll-reveal hook — mirrors LandingPage.tsx. Duplicated rather than extracted because
   the two pages will likely diverge in section count over time. */
function useScrollReveal() {
  const refs = useRef<(HTMLElement | null)[]>([]);

  const setRef = useCallback((index: number) => (el: HTMLElement | null) => {
    refs.current[index] = el;
  }, []);

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      refs.current.forEach(el => el?.classList.add('landing-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('landing-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    refs.current.forEach(el => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return setRef;
}

const SolarLandingPage: React.FC = () => {
  const setRef = useScrollReveal();
  const [searchParams] = useSearchParams();

  // Preserve upstream attribution: an arrival from /maps/solar-uk or an outbound email
  // passes ?source=... — we forward that to /login so the signup record gets the original
  // channel, not just 'for-solar'.
  const upstreamSource = searchParams.get('source');
  const registerSource = upstreamSource || 'for-solar';
  const registerHref = `/login?source=${encodeURIComponent(registerSource)}&mode=register`;

  useEffect(() => {
    track('landing_view', { pathname: '/for/solar', source: upstreamSource || null });
  }, [upstreamSource]);

  return (
    <div className="landing-page">
      <SeoMeta
        title="Interactive Installation Maps for Solar Companies | Map of Projects"
        description="Turn your solar installation history into an interactive map your customers can explore. Embed on your website in minutes. Used by UK solar installers to win more local business."
        path="/for/solar"
      />
      <a href="#main-content" className="landing-skip-link">Skip to content</a>
      <LandingNav />

      {/* ===== 1. Hero ===== */}
      <main id="main-content">
      <section className="landing-hero">
        <HeroAccumulation />
        <Container>
          <span className="landing-hero__eyebrow">For solar installers</span>
          <h1 className="landing-hero__title">
            Every install you&rsquo;ve done, <em>on one map</em>.
          </h1>
          <p className="landing-hero__subtitle">
            Pin every install to a live map — from your phone, in about a minute. Embed it on your site or share it as its own public page, and let prospects see you&rsquo;ve worked nearby.
          </p>
          <div className="landing-hero__actions">
            <Link to="/maps/solar-uk" className="landing-btn-primary">
              See the live demo →
            </Link>
            <Link to={registerHref} className="landing-btn-secondary">
              Start free — no card required
            </Link>
          </div>
          <div className="landing-hero__trust">
            No credit card. No developer. Set up in under five minutes.
          </div>
        </Container>
      </section>

      {/* ===== 2. Added from the van (interactive stepper) ===== */}
      <EaseStepperSection
        band="light"
        revealRef={setRef(0)}
        title="Added on site. Live before you pack up."
        steps={[
          { title: 'Snap a photo', desc: 'Straight from your camera roll — up to three per install.' },
          { title: 'Drop the pin', desc: 'Tap the map where the install was — or mark it approximate for privacy.' },
          { title: "It's live — everywhere", desc: 'On your public map and your website embed, instantly.' },
        ]}
        phone={{
          newTitle: 'New install',
          photoLabel: 'install photo',
          nameLabel: 'Install name',
          nameValue: '6 kW install — Bristol',
          companyName: 'Hartley Solar',
        }}
      />

      {/* ===== 3. One map, two ways to show it ===== */}
      <TwoWaysSection band="white" revealRef={setRef(11)} />

      {/* ===== 4. Near-me social proof ===== */}
      <section className="landing-section landing-section--light" ref={setRef(6)}>
        <Container>
          <div className="landing-feature landing-reveal" ref={setRef(7)}>
            <div className="landing-feature__text">
              <span className="landing-feature__eyebrow">Social proof that converts</span>
              <h3>Show prospects you&rsquo;ve done their type of install before</h3>
              <p>
                Each pin carries the work — system size, photos, write-up. A prospect weighing up a 6 kW roof-integrated system can see at a glance you&rsquo;ve delivered the same setup five times. Locations can be exact or flagged approximate, so customer privacy isn&rsquo;t a blocker.
              </p>
            </div>
            <div className="landing-feature__image">
              <img
                src="/images/landing/pin-preview.jpg"
                alt="Map pin showing a thumbnail preview on hover"
                loading="lazy"
              />
            </div>
          </div>
        </Container>
      </section>

      {/* ===== 5. Per-install detail pages ===== */}
      <section className="landing-section" ref={setRef(8)}>
        <Container>
          <div className="landing-feature landing-feature--reverse landing-reveal" ref={setRef(9)}>
            <div className="landing-feature__image">
              <img
                src="/images/landing/case-study-screenshot.png"
                alt="Detailed page for a single install"
                loading="lazy"
              />
            </div>
            <div className="landing-feature__text">
              <span className="landing-feature__eyebrow">Detail pages</span>
              <h3>A dedicated page behind every install</h3>
              <p>
                Add as much or as little as you like — a few photos and a location is enough on day one, or pile in system size, panel and inverter notes, install date, and a write-up. Or paste a link to an install page on your own site and pins open it directly. You decide how much to share.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* ===== 6. Pricing / CTA ===== */}
      <section className="landing-section landing-section--dark" ref={setRef(16)}>
        <Container>
          <div className="landing-pricing landing-reveal" ref={setRef(17)}>
            <span className="landing-section__eyebrow">Pricing</span>
            <h2 className="landing-section__title">Start free. Upgrade when the leads start coming in.</h2>
            <p className="landing-section__subtitle">
              No credit card required. Built for UK solar installers.
            </p>

            <div className="landing-pricing__card">
              <div className="landing-pricing__free-label">Free plan</div>
              <ul className="landing-pricing__features">
                <li>Up to 10 installs</li>
                <li>3 photos per install</li>
                <li>Interactive Google Maps integration</li>
                <li>Shareable link and embed snippet</li>
              </ul>
            </div>

            <div>
              <Link to={registerHref} className="landing-btn-primary">
                Start free
              </Link>
            </div>
          </div>
        </Container>
      </section>
      </main>

      <LandingFooter />
    </div>
  );
};

export default SolarLandingPage;
